/**
 * Caja — el mostrador. Aquí pasa TODO lo que tiene un botón: se cobra la cita
 * (efectivo / Yape / Plin / transferencia, con constancia opcional) y se
 * aprueba o rechaza la constancia que el paciente subió desde su app.
 * Requiere permiso pago:create; validar, además, pago:validar.
 *
 * La otra mitad es /pagos, que es el libro: histórico, búsqueda y totales, sin
 * un solo botón. La regla es esa y no tiene excepciones —si tiene botón, es
 * Caja—, porque mientras validar se hacía en /pagos las dos pantallas
 * parecían la misma y la cajera miraba la captura en una para aprobarla en la
 * otra.
 *
 * También se cobran aquí los exámenes de laboratorio. Van en su propia pestaña
 * y no mezclados con las citas porque son otra cosa: no tienen hora ni
 * profesional que espere, y el paciente suele pagarlos al salir de la consulta.
 * Lo que sí comparten es la regla: sin el pago, el laboratorio no procesa.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Banknote, FlaskConical, Image as ImageIcon, Wallet } from "lucide-react";
import { type Cita, listCitas } from "../api/citas";
import {
  type OrdenLab,
  cobrarOrden,
  listOrdenes,
  subirConstanciaOrden,
} from "../api/laboratorio";
import {
  type MetodoPago,
  type Pago,
  listPagos,
  registrarPago,
  uploadConstancia,
  validarPago,
} from "../api/pagos";
import { EtiquetaOrigen } from "../components/EtiquetaOrigen";
import { useAuth } from "../auth/AuthProvider";
import { formatApiError } from "../api/client";
import { ConstanciaViewer } from "../components/ConstanciaViewer";
import { DropZone } from "../components/DropZone";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Dialog,
  Field,
  Input,
  Label,
} from "../components/ui";
import { useZonaHoraria, useBase } from "../auth/TenantBrandingProvider";
import { fechaHora } from "../lib/fecha";

/** Los tres motivos reales por los que una captura no vale en un mostrador. */
const MOTIVOS_RECHAZO = [
  "La imagen no se ve bien",
  "El monto no coincide",
  "Esa constancia ya se usó",
];

/**
 * Con lo que se paga HOY en el mostrador. Tres, y de momento no hay más.
 *
 * Tarjeta y transferencia venían del primer commit del proyecto y no las ha
 * usado nadie: en la base hay Yape, efectivo y Plin, y cero de las otras dos.
 * Todavía no hay POS.
 *
 * SE QUITAN DE LA PANTALLA, NO DEL SISTEMA. El backend las sigue aceptando y
 * la pantalla de Pagos las sigue sabiendo escribir, porque el día que haya POS
 * se vuelven a poner aquí y lo cobrado antes tiene que seguir leyéndose.
 */
const METODOS: { value: MetodoPago; label: string }[] = [
  { value: "EFECTIVO", label: "Efectivo" },
  { value: "YAPE", label: "Yape" },
  { value: "PLIN", label: "Plin" },
];

export function CajaPage() {
  const tz = useZonaHoraria();
  const base = useBase();
  const [cobrando, setCobrando] = useState<Cita | null>(null);
  const [viendoConstancia, setViendoConstancia] = useState<Pago | null>(null);
  const navigate = useNavigate();
  const { me } = useAuth();
  const puedeVerAtencion =
    me?.is_super_admin || (me?.permisos.includes("paciente:read") ?? false);
  const [searchParams, setSearchParams] = useSearchParams();
  const citaParam = searchParams.get("cita");
  const ordenParam = searchParams.get("orden_lab");
  const puedeCobrarLab = me?.permisos.includes("laboratorio:cobrar") ?? false;
  const [cobrandoOrden, setCobrandoOrden] = useState<OrdenLab | null>(null);

  // Tres estados y tres pestañas: lo que falta cobrar, lo que llegó con
  // constancia y espera visto bueno, y lo ya cobrado. Sin esto la caja solo
  // sabía mirar hacia adelante y nunca podía cuadrar lo del día. La cuarta son
  // los exámenes, que no son citas.
  const pestana = (searchParams.get("ver") ??
    (ordenParam ? "laboratorio" : "cobrar")) as
    | "cobrar"
    | "validar"
    | "cobrado"
    | "laboratorio";

  // Se vuelve a preguntar solo, cada medio minuto.
  //
  // Caja es una pantalla que se deja abierta el turno entero, y lo que enseña
  // cambia sin que la cajera toque nada: un paciente sube su Yape desde el
  // celular y esa cita deja de ser "por cobrar" para pasar a "por validar".
  // Sin esto, ella seguía viendo la foto del momento en que abrió la pestaña,
  // y si el paciente se acercaba a ventanilla le cobraba por segunda vez.
  //
  // Medio minuto es lo que tarda alguien en llegar del celular al mostrador.
  // Y `refetchIntervalInBackground` queda en falso a propósito: con la pestaña
  // detrás no hay nadie mirando, y preguntar igual es gastar batería y
  // conexión para nada — al volver a ella se refresca de todos modos.
  const REFRESCO_CAJA = 30_000;

  const { data: citas = [], isLoading } = useQuery({
    queryKey: ["citas", "por_cobrar"],
    queryFn: () => listCitas({ por_cobrar: true }),
    refetchInterval: REFRESCO_CAJA,
  });
  const { data: pagos = [], isLoading: cargandoPagos } = useQuery({
    queryKey: ["pagos", "todos"],
    queryFn: () => listPagos(),
    refetchInterval: REFRESCO_CAJA,
  });

  const porValidar = pagos.filter((p) => p.estado === "PENDIENTE_VALIDACION");
  const cobrados = pagos.filter((p) => p.estado === "PAGADO");
  // Una cita con constancia pendiente aparece en su propia pestaña, no como
  // trabajo por hacer en Caja: ahí ya no hay nada que cobrar, hay que validar.
  //
  // Y ordenadas por lo último agendado, no por fecha de cita: lo que acaba de
  // registrar recepción tiene que salir arriba. Ordenado por fecha de cita, un
  // cobro nuevo para dentro de dos semanas caía al fondo de la lista y parecía
  // que nunca había llegado.
  const porCobrar = citas
    .filter((c) => c.pago_estado !== "PENDIENTE_VALIDACION")
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

  const { data: ordenesLab = [], isLoading: cargandoLab } = useQuery({
    queryKey: ["lab", "por_cobrar"],
    queryFn: () => listOrdenes({ por_cobrar: true }),
    enabled: puedeCobrarLab,
    refetchInterval: REFRESCO_CAJA,
  });

  // `avisa` marca las pestañas donde hay trabajo esperando, para que se vean
  // en rojo y no como un número más.
  //
  // Cobrados no avisa: es un archivo, no una cola. Un contador rojo con 22 al
  // lado de dos que dicen 0 enseña a ignorar el rojo, que es lo contrario de
  // lo que una alerta tiene que conseguir.
  // El paciente subió su captura y al llegar al mostrador prefiere pagar en
  // efectivo. Se descarta el intento y se abre el cobro normal, aquí mismo.
  //
  // Descartar NO es cobrar: si el paciente se arrepiente otra vez —llegó sin
  // efectivo, lo paga por la app— la cita vuelve a estar disponible para las
  // dos vías. Por eso el pago descartado se borra en el backend en vez de
  // quedarse ocupando el sitio.
  const [pagandoEnCaja, setPagandoEnCaja] = useState<Pago | null>(null);
  const queryClient = useQueryClient();

  // Aprobar la constancia SIN salir de Caja.
  //
  // Antes esto era un enlace a /pagos: la cajera miraba la captura en una
  // pantalla y aprobaba en otra, con el paciente esperando. Y de paso hacía
  // que las dos pantallas parecieran la misma cosa, porque la bandeja de
  // trabajo estaba repartida entre ambas. Validar es una ACCIÓN, y las
  // acciones viven en Caja.
  const [rechazando, setRechazando] = useState<Pago | null>(null);
  // Por qué se rechaza. Va al paciente tal cual, así que se ofrecen los tres
  // motivos reales de un mostrador —y no una caja de texto vacía delante de
  // una cola—, con la posibilidad de escribir otro.
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [errorValidar, setErrorValidar] = useState<string | null>(null);

  const resolver = useMutation({
    mutationFn: ({
      pago,
      aprobar,
      motivo,
    }: {
      pago: Pago;
      aprobar: boolean;
      motivo?: string;
    }) => validarPago(pago.id, aprobar, motivo),
    onSuccess: async () => {
      setRechazando(null);
      setMotivoRechazo("");
      setErrorValidar(null);
      await queryClient.invalidateQueries({ queryKey: ["pagos"] });
      await queryClient.invalidateQueries({ queryKey: ["citas"] });
      await queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
    },
    onError: (e) => setErrorValidar(formatApiError(e, "No se pudo validar el pago.")),
  });

  const pasarACaja = useMutation({
    mutationFn: (pago: Pago) => validarPago(pago.id, false),
    onSuccess: async (_r, pago) => {
      setPagandoEnCaja(null);
      await queryClient.invalidateQueries({ queryKey: ["pagos"] });
      await queryClient.invalidateQueries({ queryKey: ["citas"] });
      await queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
      // Y se abre el cobro de esa cita sin que haya que buscarla en la otra
      // pestaña: el paciente está delante.
      setSearchParams({ ver: "cobrar", cita: String(pago.cita_id) }, { replace: true });
    },
  });

  const PESTANAS = [
    { id: "cobrar", label: "Por cobrar", total: porCobrar.length, avisa: true },
    { id: "validar", label: "Por validar", total: porValidar.length, avisa: true },
    { id: "cobrado", label: "Cobrados", total: cobrados.length, avisa: false },
    ...(puedeCobrarLab
      ? [
          {
            id: "laboratorio",
            label: "Exámenes",
            total: ordenesLab.length,
            avisa: true,
          },
        ]
      : []),
  ] as const;

  // Si vienen con ?cita=, se abre su cobro directamente en cuanto carga.
  useEffect(() => {
    if (!citaParam || cobrando) return;
    const c = citas.find((x) => String(x.id) === citaParam);
    if (c) setCobrando(c);
  }, [citaParam, citas, cobrando]);

  // Lo mismo desde la bandeja de exámenes por cobrar.
  useEffect(() => {
    if (!ordenParam || cobrandoOrden) return;
    const o = ordenesLab.find((x) => String(x.id) === ordenParam);
    if (o) setCobrandoOrden(o);
  }, [ordenParam, ordenesLab, cobrandoOrden]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Wallet className="w-6 h-6 text-[color:var(--color-link)]" />
          Caja
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Registra el cobro para que la cita pueda ser atendida.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {PESTANAS.map((t) => {
          const activa = pestana === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setSearchParams({ ver: t.id }, { replace: true })}
              className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition ${
                activa
                  ? "border-[color:var(--color-link)] text-[color:var(--color-link)]"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
              aria-current={activa ? "page" : undefined}
            >
              {t.label}
              <span
                className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                  t.avisa && t.total > 0
                    ? "bg-red-600 text-white"
                    : activa
                      ? "bg-slate-100 text-slate-700"
                      : "bg-slate-100 text-slate-500"
                }`}
              >
                {t.total}
              </span>
            </button>
          );
        })}
      </div>

      {(pestana === "validar" || pestana === "cobrado") && (
        <PagosTabla
          onPagarEnCaja={pestana === "validar" ? (p) => setPagandoEnCaja(p) : undefined}
          onAprobar={
            pestana === "validar"
              ? (p) => resolver.mutate({ pago: p, aprobar: true })
              : undefined
          }
          onRechazar={pestana === "validar" ? setRechazando : undefined}
          resolviendo={resolver.isPending}
          error={pestana === "validar" ? errorValidar : null}
          pagos={pestana === "validar" ? porValidar : cobrados}
          cargando={cargandoPagos}
          vacio={
            pestana === "validar"
              ? "Nada esperando validación."
              : "Todavía no se cobró nada."
          }
          tz={tz}
          accion={pestana === "validar"}
          onVerConstancia={setViendoConstancia}
        />
      )}

      {/* Se confirma porque descarta lo que el paciente subió, y porque suele
          pulsarse con él delante: conviene que la cajera lea qué va a pasar
          antes, no después. */}
      <ConfirmDialog
        open={pagandoEnCaja != null}
        onClose={() => setPagandoEnCaja(null)}
        onConfirm={() => pagandoEnCaja && pasarACaja.mutate(pagandoEnCaja)}
        loading={pasarACaja.isPending}
        variant="primary"
        title="Cobrar en el mostrador"
        description="Se descarta la captura que subió y se abre el cobro. Si al final prefiere pagarlo por la app, puede volver a subirla desde su teléfono."
        detalle={
          pagandoEnCaja && (
            <>
              {pagandoEnCaja.paciente_nombre} ·{" "}
              <span className="font-medium">
                S/ {Number(pagandoEnCaja.monto).toFixed(2)}
              </span>
            </>
          )
        }
        confirmLabel="Sí, cobrar aquí"
        cancelLabel="No, dejarlo"
      />

      {/* Rechazar y "prefiere pagar en caja" llaman al mismo endpoint, pero no
          son lo mismo para quien está delante: uno dice "cambió de idea" y el
          otro "esta captura no vale". Que la cajera lea cuál está pulsando. */}
      <Dialog
        open={rechazando != null}
        onClose={() => {
          setRechazando(null);
          setMotivoRechazo("");
        }}
        title="Rechazar la constancia"
        description="La cita vuelve a quedar por cobrar y el paciente puede subir otra desde su teléfono."
      >
        {rechazando && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              {rechazando.paciente_nombre} ·{" "}
              <span className="font-medium">
                S/ {Number(rechazando.monto).toFixed(2)}
              </span>
            </p>

            {/* EL MOTIVO LLEGA A SU TELÉFONO.

                Sin él, en la app del paciente rechazar es indistinguible de
                que no hubiera pasado nada: vuelve a leer "Pasa por caja" y
                sube la misma captura otra vez. Los tres botones son los tres
                motivos de verdad; escribir es para el resto. */}
            <div>
              <Label>¿Por qué? El paciente lo verá en su app.</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {MOTIVOS_RECHAZO.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMotivoRechazo(m)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      motivoRechazo === m
                        ? "border-slate-800 bg-slate-800 text-white"
                        : "border-slate-300 text-slate-600 hover:border-slate-500"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <Input
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                placeholder="O escribe el motivo"
                maxLength={255}
                className="mt-2"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setRechazando(null);
                  setMotivoRechazo("");
                }}
              >
                Volver
              </Button>
              <Button
                variant="danger"
                loading={resolver.isPending}
                onClick={() =>
                  resolver.mutate({
                    pago: rechazando,
                    aprobar: false,
                    motivo: motivoRechazo,
                  })
                }
              >
                Rechazar
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      {pestana === "cobrar" && (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Banknote className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Citas por cobrar</span>
            <span className="ml-auto text-xs text-slate-500">{porCobrar.length}</span>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && (
            <div className="px-6 py-12 text-center text-sm text-slate-400">Cargando…</div>
          )}
          {!isLoading && porCobrar.length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              No hay citas pendientes de cobro.
            </div>
          )}
          {porCobrar.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <Th>Fecha</Th>
                    <Th>Paciente</Th>
                    <Th>Especialidad</Th>
                    <Th>Profesional</Th>
                    <Th>Monto</Th>
                    <Th> </Th>
                  </tr>
                </thead>
                <tbody>
                  {porCobrar.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-6 py-3 text-slate-600">{fechaHora(c.inicio, tz)}</td>
                      <td className="px-6 py-3 font-medium text-slate-900">{c.paciente_nombre}</td>
                      <td className="px-6 py-3">
                        <Badge>{c.especialidad_nombre}</Badge>
                      </td>
                      <td className="px-6 py-3 text-slate-600">{c.profesional_nombre}</td>
                      <td className="px-6 py-3 font-mono">
                        {c.monto != null ? `S/ ${Number(c.monto).toFixed(2)}` : "—"}
                        {c.pago_estado === "PENDIENTE_VALIDACION" && (
                          <span className="ml-2">
                            <Badge variant="warning">Constancia por validar</Badge>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right">
                        {/* Aquí no puede haber una cita con constancia
                            pendiente: `porCobrar` las aparta a su pestaña. La
                            rama que mandaba a /pagos a validarla llevaba
                            tiempo siendo código muerto. */}
                        <Button variant="success" onClick={() => setCobrando(c)}>
                          Registrar pago
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      )}

      {pestana === "laboratorio" && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">
                Exámenes por cobrar
              </span>
              <span className="ml-auto text-xs text-slate-500">
                {ordenesLab.length}
              </span>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {cargandoLab && (
              <div className="px-6 py-12 text-center text-sm text-slate-400">
                Cargando…
              </div>
            )}
            {!cargandoLab && ordenesLab.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-slate-500">
                No hay exámenes pendientes de cobro.
              </div>
            )}
            {ordenesLab.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-y border-slate-200 bg-slate-50">
                    <tr>
                      <Th>Solicitada</Th>
                      <Th>Paciente</Th>
                      <Th>Exámenes</Th>
                      <Th>Solicitó</Th>
                      <Th>Monto</Th>
                      <Th> </Th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordenesLab.map((o) => (
                      <tr
                        key={o.id}
                        className="border-b border-slate-100 hover:bg-slate-50/50"
                      >
                        <td className="px-6 py-3 text-slate-600">
                          {fechaHora(o.fecha, tz)}
                        </td>
                        <td className="px-6 py-3 font-medium text-slate-900">
                          {o.paciente_nombre}
                          {o.urgente && (
                            <span className="ml-2">
                              <Badge variant="danger">Urgente</Badge>
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3 text-slate-600">
                          {o.items.map((i) => i.descripcion).join(", ")}
                        </td>
                        <td className="px-6 py-3 text-slate-600">
                          {o.profesional_nombre ?? "—"}
                        </td>
                        <td className="px-6 py-3 font-mono">
                          S/ {Number(o.total).toFixed(2)}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <Button
                            variant="success"
                            onClick={() => setCobrandoOrden(o)}
                          >
                            Registrar pago
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <CobrarOrdenDialog
        orden={cobrandoOrden}
        onClose={() => {
          setCobrandoOrden(null);
          if (ordenParam) setSearchParams({ ver: "laboratorio" }, { replace: true });
        }}
      />

      <ConstanciaViewer
        pagoId={viendoConstancia?.id ?? null}
        titulo={
          viendoConstancia
            ? `${viendoConstancia.paciente_nombre ?? "—"} · ${viendoConstancia.metodo} · S/ ${Number(viendoConstancia.monto).toFixed(2)}`
            : ""
        }
        onClose={() => setViendoConstancia(null)}
      />

      <RegistrarPagoDialog
        cita={cobrando}
        onClose={() => {
          setCobrando(null);
          // Limpia el parámetro para que cerrar y volver no reabra el diálogo.
          if (citaParam) setSearchParams({}, { replace: true });
        }}
        onCobrado={(c) => {
          // Si entró desde una atención concreta, ahí vuelve. Dejarlo tirado en
          // la lista de caja lo obliga a buscar de nuevo al paciente que acaba
          // de tener delante. Salvo que sea una cuenta de caja pura: para ella
          // la pantalla de la atención está cerrada y su sitio es esta lista.
          if (citaParam && puedeVerAtencion) navigate(`${base}/citas/${c.id}`);
        }}
      />
    </div>
  );
}

/** Lo ya cobrado o lo que espera visto bueno. Misma tabla, distinto filtro. */
function PagosTabla({
  pagos,
  cargando,
  vacio,
  tz,
  accion,
  onPagarEnCaja,
  onAprobar,
  onRechazar,
  resolviendo = false,
  error,
  onVerConstancia,
}: {
  pagos: Pago[];
  cargando: boolean;
  vacio: string;
  tz: string;
  accion: boolean;
  /** Descarta la captura y abre el cobro en el mostrador, sin salir de aquí. */
  onPagarEnCaja?: (pago: Pago) => void;
  onAprobar?: (pago: Pago) => void;
  onRechazar?: (pago: Pago) => void;
  resolviendo?: boolean;
  error?: string | null;
  onVerConstancia: (p: Pago) => void;
}) {
  return (
    <Card>
      <CardBody className="p-0">
        {error && (
          <div className="px-6 pt-4">
            <Alert>{error}</Alert>
          </div>
        )}
        {cargando && (
          <div className="px-6 py-12 text-center text-sm text-slate-400">Cargando…</div>
        )}
        {!cargando && pagos.length === 0 && (
          <div className="px-6 py-12 text-center text-sm text-slate-500">{vacio}</div>
        )}
        {pagos.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-y border-slate-200 bg-slate-50">
                <tr>
                  <Th>Cita</Th>
                  <Th>Paciente</Th>
                  <Th>Especialidad</Th>
                  <Th>Método</Th>
                  <Th>Cobrado</Th>
                  <Th>Monto</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {pagos.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-6 py-3 text-slate-600">
                      {p.cita_inicio ? fechaHora(p.cita_inicio, tz) : "—"}
                    </td>
                    <td className="px-6 py-3 font-medium text-slate-900">
                      {p.paciente_nombre ?? "—"}
                    </td>
                    <td className="px-6 py-3">
                      {p.especialidad_nombre ? <Badge>{p.especialidad_nombre}</Badge> : "—"}
                    </td>
                    <td className="px-6 py-3 text-slate-600">
                      {p.metodo}
                      <EtiquetaOrigen origen={p.origen} />
                      {p.tiene_constancia && (
                        <button
                          type="button"
                          onClick={() => onVerConstancia(p)}
                          className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-[color:var(--color-link)] hover:underline"
                        >
                          <ImageIcon className="h-3.5 w-3.5" />
                          ver constancia
                        </button>
                      )}
                    </td>
                    {/* Cuándo entró la plata, que es distinto de cuándo es la
                        cita: para cuadrar la caja del día manda esta hora. */}
                    <td className="px-6 py-3 text-slate-600">
                      {fechaHora(p.created_at, tz)}
                    </td>
                    <td className="px-6 py-3 font-mono">
                      S/ {Number(p.monto).toFixed(2)}
                    </td>
                    <td className="px-6 py-3 text-right">
                      {accion ? (
                        <div className="flex flex-wrap items-center justify-end gap-3">
                          {/* "Prefiere pagar en caja" y no "Rechazar".

                              Rechazar es para cuando la captura NO VALE, y eso
                              acusa a quien no hizo nada malo: el paciente que
                              llega al mostrador y cambia de idea no falsificó
                              ningún Yape. Con la palabra equivocada, la cajera
                              duda de si puede pulsarla delante de él. */}
                          {onPagarEnCaja && (
                            <button
                              type="button"
                              onClick={() => onPagarEnCaja(p)}
                              className="text-sm font-medium text-slate-600 hover:underline"
                            >
                              Prefiere pagar en caja
                            </button>
                          )}
                          {/* Rechazar sí es "esta captura no vale" y por eso va
                              apagado y con confirmación: es la única de las tres
                              salidas que deja al paciente sin su comprobante. */}
                          {onRechazar && (
                            <button
                              type="button"
                              onClick={() => onRechazar(p)}
                              disabled={resolviendo}
                              className="text-sm font-medium text-slate-500 hover:text-red-700 hover:underline disabled:opacity-50"
                            >
                              Rechazar
                            </button>
                          )}
                          {onAprobar && (
                            <Button
                              variant="success"
                              onClick={() => onAprobar(p)}
                              disabled={resolviendo}
                            >
                              Aprobar
                            </Button>
                          )}
                        </div>
                      ) : (
                        <Badge variant="success">Cobrado</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
      {children}
    </th>
  );
}

/**
 * Cobro de una orden de laboratorio.
 *
 * Sin constancia ni monto editable: el precio salió del catálogo cuando el
 * médico pidió los exámenes y ahí quedó congelado. Caja cobra lo que dice la
 * orden, no lo que se le ocurra en el momento.
 */
function CobrarOrdenDialog({
  orden,
  onClose,
}: {
  orden: OrdenLab | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [metodo, setMetodo] = useState<MetodoPago>("EFECTIVO");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (orden) {
      setMetodo("EFECTIVO");
      setFile(null);
      setError(null);
    }
  }, [orden]);

  // Lo mismo que en una cita: lo que no es efectivo deja un comprobante en una
  // pantalla, y sin él el cobro es la palabra de quien lo marcó. Un examen se
  // cobra tan a menudo y por importes parecidos; no hay motivo para que sea el
  // único cobro del policlínico que no enseña nada.
  const digital = metodo !== "EFECTIVO";

  const mutation = useMutation({
    mutationFn: async () => {
      const cobrada = await cobrarOrden(orden!.id, metodo);
      if (file) await subirConstanciaOrden(orden!.id, file);
      return cobrada;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab"] });
      // El laboratorio la ve caer en su cola en cuanto se paga.
      queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
      onClose();
    },
    onError: (err) => setError(formatApiError(err, "No se pudo cobrar la orden.")),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Dialog
      open={orden !== null}
      onClose={onClose}
      title="Cobrar exámenes"
      description={
        orden
          ? `${orden.paciente_nombre} · solicitado por ${orden.profesional_nombre ?? "—"}`
          : undefined
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {orden && (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {orden.items.map((i) => (
              <li
                key={i.id}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
              >
                <span className="text-slate-800">{i.descripcion}</span>
                <span className="font-mono text-slate-700">
                  S/ {Number(i.precio).toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        )}

        <Field label="Método *" htmlFor="lab_met">
          <select
            id="lab_met"
            value={metodo}
            onChange={(e) => setMetodo(e.target.value as MetodoPago)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            {METODOS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>

        {digital && (
          <Field
            label="Constancia *"
            htmlFor="lab_const"
            hint="La captura de la operación. Sin ella el cobro no queda respaldado."
          >
            <DropZone id="lab_const" file={file} onFile={setFile} />
          </Field>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Total</p>
            <p className="font-mono text-xl font-bold text-slate-900">
              S/ {Number(orden?.total ?? 0).toFixed(2)}
            </p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="success"
              loading={mutation.isPending}
              // Sin constancia no se cobra por Yape, igual que en una cita.
              disabled={digital && file === null}
            >
              Registrar pago
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function RegistrarPagoDialog({
  cita,
  onClose,
  onCobrado,
}: {
  cita: Cita | null;
  onClose: () => void;
  /** Cobro registrado. La página decide a dónde devolver al usuario. */
  onCobrado?: (cita: Cita) => void;
}) {
  const queryClient = useQueryClient();
  const [metodo, setMetodo] = useState<MetodoPago>("EFECTIVO");
  const [monto, setMonto] = useState("");
  const [nota, setNota] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cita) {
      setMetodo("EFECTIVO");
      setMonto(cita.monto != null ? String(Number(cita.monto)) : "");
      setNota("");
      setFile(null);
      setError(null);
    }
  }, [cita]);

  const digital = metodo !== "EFECTIVO";

  const mutation = useMutation({
    mutationFn: async () => {
      const pago = await registrarPago({
        cita_id: cita!.id,
        metodo,
        monto: monto === "" ? undefined : monto,
        nota: nota || undefined,
      });
      if (file) await uploadConstancia(pago.id, file);
      return pago;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["citas"] });
      queryClient.invalidateQueries({ queryKey: ["cita", cita!.id] });
      // Sin esto la pestaña "Cobrados" seguía mostrando la lista vieja y el
      // cobro recién hecho solo aparecía recargando la página a mano.
      queryClient.invalidateQueries({ queryKey: ["pagos"] });
      // La bandeja de triaje depende de esto: al cobrar, el paciente pasa a
      // enfermería y tiene que aparecerle ya.
      queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
      const cobrada = cita!;
      onClose();
      onCobrado?.(cobrada);
    },
    onError: (err) => setError(formatApiError(err, "No se pudo registrar el pago.")),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Dialog
      open={cita !== null}
      onClose={onClose}
      title="Registrar pago"
      description={
        cita
          ? `${cita.especialidad_nombre} — ${cita.profesional_nombre} · ${cita.paciente_nombre}`
          : undefined
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Método *" htmlFor="pg_met">
            <select
              id="pg_met"
              value={metodo}
              onChange={(e) => setMetodo(e.target.value as MetodoPago)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              {METODOS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Monto (S/) *" htmlFor="pg_monto">
            <Input
              id="pg_monto"
              type="number"
              min="0"
              step="0.01"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              required
              className="font-mono"
            />
          </Field>
        </div>

        {digital && (
          <Field
            label="Constancia (Yape/Plin/transferencia)"
            htmlFor="pg_const"
            hint="Opcional. Si la subes, el pago queda pendiente de validación."
          >
            <DropZone id="pg_const" file={file} onFile={setFile} />
          </Field>
        )}

        <Field label="Nota" htmlFor="pg_nota">
          <Input
            id="pg_nota"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            maxLength={500}
            placeholder="Opcional (ej. N° de operación)"
          />
        </Field>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="success" loading={mutation.isPending}>
            Confirmar pago
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
